/**
 * Invoice List Component
 * Shows all invoices for a job with status and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceService, Invoice, InvoiceStatus } from '@/services/invoice.service';

interface InvoiceListProps {
  jobId: string;
  userRole?: 'applicant' | 'provider'; // Determines which actions are shown
}

export const InvoiceList: React.FC<InvoiceListProps> = ({ jobId, userRole = 'applicant' }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const invoicesData = await invoiceService.getJobInvoices(jobId);
      setInvoices(invoicesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const formatAmount = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getStatusBadge = (status: InvoiceStatus) => {
    const badges = {
      draft: {
        label: 'Draft',
        className: 'bg-neutral-100 text-neutral-700',
      },
      sent: {
        label: 'Sent',
        className: 'bg-blue-100 text-blue-700',
      },
      paid: {
        label: 'Paid',
        className: 'bg-green-100 text-green-700',
      },
      cancelled: {
        label: 'Cancelled',
        className: 'bg-red-100 text-red-700',
      },
    };

    const badge = badges[status] || badges.draft;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const handleViewInvoice = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  const handlePayInvoice = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}/pay`);
  };

  const handleGenerateInvoice = () => {
    navigate(`/jobs/${jobId}/invoices/new`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
        <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-red-800">{error}</p>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Invoices Yet</h3>
        <p className="text-neutral-600 mb-6">
          {userRole === 'provider'
            ? 'Generate an invoice from material tickets to get started.'
            : 'Invoices will appear here once the service provider generates them.'}
        </p>
        {userRole === 'provider' && (
          <button onClick={handleGenerateInvoice} className="btn-primary">
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Generate Invoice
          </button>
        )}
      </div>
    );
  }

  // Desktop: Table view
  const TableView = () => (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Invoice #</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Date</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Status</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-neutral-700">Amount</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-neutral-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(invoice => (
            <tr key={invoice.id} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="py-3 px-4">
                <span className="text-sm font-medium text-neutral-900">
                  {invoice.invoice_number || invoice.id.slice(0, 8)}
                </span>
              </td>
              <td className="py-3 px-4 text-sm text-neutral-600">
                {formatDate(invoice.created_at)}
              </td>
              <td className="py-3 px-4">
                {getStatusBadge(invoice.status)}
              </td>
              <td className="py-3 px-4 text-sm font-medium text-neutral-900 text-right">
                {formatAmount(invoice.total_amount_cents)}
              </td>
              <td className="py-3 px-4 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => handleViewInvoice(invoice.id)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View
                  </button>
                  {invoice.status === 'sent' && userRole === 'applicant' && (
                    <button
                      onClick={() => handlePayInvoice(invoice.id)}
                      className="text-sm bg-accent-600 text-white px-3 py-1 rounded-md hover:bg-accent-700 font-medium"
                    >
                      Pay Now
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Mobile: Card view
  const CardView = () => (
    <div className="md:hidden space-y-4">
      {invoices.map(invoice => (
        <div key={invoice.id} className="card p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="font-medium text-neutral-900">
                {invoice.invoice_number || invoice.id.slice(0, 8)}
              </p>
              <p className="text-sm text-neutral-600 mt-1">{formatDate(invoice.created_at)}</p>
            </div>
            {getStatusBadge(invoice.status)}
          </div>

          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-neutral-600">Amount</span>
            <span className="text-lg font-semibold text-neutral-900">
              {formatAmount(invoice.total_amount_cents)}
            </span>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleViewInvoice(invoice.id)}
              className="flex-1 btn-secondary text-sm py-2"
            >
              View Details
            </button>
            {invoice.status === 'sent' && userRole === 'applicant' && (
              <button
                onClick={() => handlePayInvoice(invoice.id)}
                className="flex-1 bg-accent-600 text-white px-4 py-2 rounded-md hover:bg-accent-700 font-medium text-sm"
              >
                Pay Now
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-neutral-900">Invoices</h2>
        {userRole === 'provider' && (
          <button onClick={handleGenerateInvoice} className="btn-primary">
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Generate Invoice
          </button>
        )}
      </div>

      <div className="card">
        <TableView />
        <CardView />
      </div>

      {/* Invoice Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-sm text-neutral-600 mb-1">Total Invoiced</p>
          <p className="text-2xl font-semibold text-neutral-900">
            {formatAmount(invoices.reduce((sum, inv) => sum + inv.total_amount_cents, 0))}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-neutral-600 mb-1">Paid</p>
          <p className="text-2xl font-semibold text-green-600">
            {formatAmount(
              invoices
                .filter(inv => inv.status === 'paid')
                .reduce((sum, inv) => sum + inv.total_amount_cents, 0)
            )}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-neutral-600 mb-1">Outstanding</p>
          <p className="text-2xl font-semibold text-amber-600">
            {formatAmount(
              invoices
                .filter(inv => inv.status === 'sent')
                .reduce((sum, inv) => sum + inv.total_amount_cents, 0)
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
